import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { orderExpertEvaluation } from 'store/entities/professionalReport/thunks';
import { setExpertReportModalOpen } from 'store/entities/professionalReport';

import { Button, Row, Col, Typography, Space, Radio, Input, message, Form, Divider } from 'antd';
import Modal from 'components/reusable/Modal';
import LoadingSpinner from 'components/reusable/LoadingSpinner';
import { ReactComponent as Close } from 'assets/img/icons/close.svg';
import { ReactComponent as ExpertReport } from 'assets/img/icons/expertModal.svg';
import useFormatDate from '../../../hooks/format/useFormatDate';
import { BlockHeader } from '../../../components/reusable/BlockHeader';
import { InputDataRow } from '../../../components/reusable/CompanyDataCard';
import SuccessModal from '../../../components/reusable/SuccessModal';
import { setSuccessModalVisible } from '../../../store/entities/administration';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const OrderExpertReport = () => {
  const { companyId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formatDate = useFormatDate();
  const [form] = Form.useForm();

  const { session } = useSelector(state => state.user);
  const { expertReportModalOpen, orderId, orderExpertEvaluationPending, basicData, expertEvaluation } = useSelector(
    state => state.professionalReport
  );

  const [mentionCustomerCompany, setMentionCustomerCompany] = useState(true);
  const [orderDetails, setOrderDetails] = useState('');
  const orderReport = async () => {
    const order = {
      mentionCustomerCompany,
      orderDetails,
      customerId: 0,
      creditinfoId: 0,
      companyCode: 0,
    };

    const submitResult = await dispatch(orderExpertEvaluation({ orderId, orderDetails: order }));

    if (orderExpertEvaluation.fulfilled.match(submitResult)) {
      message.success(t('professionalReport.companyRatings.orderExpertReport.success.message'), 5);
      dispatch(setExpertReportModalOpen(false));
    }
  };

  return (
    <>
      {!expertEvaluation?.isExpired && expertEvaluation?.createdDate ? (
        <>
          <SuccessModal
            size="lg"
            onCancel={() => dispatch(setExpertReportModalOpen(false))}
            isOpen={expertReportModalOpen}
            type={'expertValue'}
            text={
              <Trans
                i18nKey="professionalReport.companyRatings.orderExpertReport.notExpired"
                values={{ date: formatDate(expertEvaluation?.createdDate) }}
                components={{ bold: <strong className="bigger-font" /> }}
              />
            }
          />
        </>
      ) : (
        <Modal
          isOpen={expertReportModalOpen}
          setIsOpen={isOpen => dispatch(setExpertReportModalOpen(isOpen))}
          // closable={orderExpertEvaluationPending}
          width={840}
          size="md"
          onCancel={() => dispatch(setExpertReportModalOpen(false))}
          className="new-debt"
          secondaryTitle={
            <BlockHeader
              title={t('professionalReport.companyRatings.orderExpertReport.title')}
              icon={<ExpertReport style={{ height: 30, width: 30 }} />}
            />
          }
        >
          <LoadingSpinner size="large" spinning={orderExpertEvaluationPending}>
            <>
              <Form form={form} labelCol={{ span: 24 }} className="new-debt" requiredMark={false} layout="inline">
                <Row gutter={[16, 4]} style={{ marginBottom: 16 }}>
                  <Col span={24}>
                    <InputDataRow
                      label={t(`professionalReport.companyRatings.orderExpertReport.label.client`)}
                      input={`${session?.companyName}, ${session?.fullName}`}
                    />
                  </Col>
                  <Col span={24}>
                    <InputDataRow
                      label={t(`professionalReport.companyRatings.orderExpertReport.label.clientCode`)}
                      input={session?.companyCode}
                    />
                  </Col>
                  <Col span={24}>
                    <InputDataRow
                      label={t(`professionalReport.companyRatings.orderExpertReport.label.clientEmail`)}
                      input={session?.email}
                    />
                  </Col>
                  <Col span={24}>
                    <InputDataRow
                      label={t(`professionalReport.companyRatings.orderExpertReport.label.order`)}
                      input={`${basicData?.basicInformation?.companyName} 
              (${t('professionalReport.companyRatings.orderExpertReport.label.c.c')} 
              ${companyId}) ${t('professionalReport.companyRatings.orderExpertReport.label.expert.evaluation')}`}
                    />
                  </Col>
                  <Col span={24}>
                    <InputDataRow
                      label={t(`professionalReport.companyRatings.orderExpertReport.label.orderPrice`)}
                      input={t('professionalReport.companyRatings.orderExpertReport.order.price')}
                    />
                  </Col>
                  <Col span={24}>
                    <InputDataRow
                      label={t(`professionalReport.companyRatings.orderExpertReport.label.orderExecutionTime`)}
                      input={t('professionalReport.companyRatings.orderExpertReport.label.execution.time')}
                    />
                  </Col>
                  {expertEvaluation?.createdDate && (
                    <Col span={24}>
                      <InputDataRow
                        label={t('professionalReport.companyRatings.orderExpertReport.createdDate')}
                        input={formatDate(expertEvaluation?.createdDate)}
                      />
                    </Col>
                  )}
                  <Divider className="space-vertical-xs" />
                  <Col span={24}>
                    <Paragraph style={{ color: '#999999' }}>
                      {t('professionalReport.companyRatings.orderExpertReport.description.mention')}
                    </Paragraph>
                  </Col>
                  <Col span={24}>
                    <InputDataRow
                      label={t(`professionalReport.companyRatings.orderExpertReport.label.mention`)}
                      input={
                        <div>
                          <span>
                            {t('global.false')}{' '}
                            <Radio checked={!mentionCustomerCompany} onClick={e => setMentionCustomerCompany(false)} />
                          </span>
                          <span>
                            {t('global.true')}{' '}
                            <Radio checked={mentionCustomerCompany} onClick={e => setMentionCustomerCompany(true)} />
                          </span>
                        </div>
                      }
                    />
                  </Col>
                  <Col span={24}>
                    <TextArea
                      key="comments"
                      value={orderDetails}
                      onChange={e => setOrderDetails(e.target.value)}
                      rows={5}
                      placeholder={t('professionalReport.companyRatings.orderExpertReport.placeholder.comments')}
                    />
                  </Col>
                </Row>
                <Row gutter={[16]} align="end" style={{ width: '100%' }}>
                  <Col>
                    <Button size="small" htmlType="button" onClick={() => dispatch(setExpertReportModalOpen(false))}>
                      {t('global.cancel')}
                    </Button>
                  </Col>
                  <Col>
                    <Button className="save" size="small" htgmlType="button" onClick={orderReport}>
                      {t('global.order')}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          </LoadingSpinner>
        </Modal>
      )}
    </>
  );
};

export default OrderExpertReport;
